import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import type { User } from "../../types/DB2";
import { useUserData } from "../../context/UserDataFetcher";

export type FormUserType = Partial<Omit<User, "role">> & {
  role?: string;
  password?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  // changed: onSave now accepts FormUserType so caller (AdminDashboard) can decide single vs bulk behavior
  onSave: (user: FormUserType) => Promise<void> | void;
  user?: User | null;
  // new: indicate bulk edit mode so the dialog can show appropriate title/intent
  bulk?: boolean;
  roleOptions?: string[];
};

const emailRegex = /^\S+@\S+\.\S+$/;

const DEFAULT_ROLE_OPTIONS = ["manufacturer", "dealer", "driver", "manufacturerRep"];

const ROLE_LABEL_MAP: Record<string, string> = {
  manufacturer: "Manufacturer",
  dealer: "Dealer",
  driver: "Driver",
  manufacturerRep: "Rep",
  admin: "Admin",
};

const formatRoleLabel = (role: string) =>
  ROLE_LABEL_MAP[role] ||
  role
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const EditUserModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  user,
  bulk,
  roleOptions,
}) => {
  const [form, setForm] = useState<FormUserType>({});
  const initialRef = useRef<FormUserType>({});
  // confirm password state
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // new file state + preview
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const currentUserData = useUserData();
  const canEditAdminFlag = !!currentUserData?.isManufacturerPrincipal;
  const effectiveRole = form.role ?? user?.role ?? undefined;
  const isDriverRoleSelected =
    typeof effectiveRole === "string" && effectiveRole.trim().toLowerCase() === "driver";
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isBulk = !!bulk;
  const isCreate = !user && !isBulk;
  const isEditSingle = !!user && !isBulk;
  const availableRoles =
    roleOptions && roleOptions.length > 0
      ? Array.from(new Set(roleOptions))
      : DEFAULT_ROLE_OPTIONS;

  useEffect(() => {
    // initialize form + snapshot for change detection
    if (user) {
      const initial = {
        ...user,
        createdAt: user.createdAt,
      } as FormUserType;
      setForm(initial);
      initialRef.current = initial;
      setPreviewUrl(user.profilePicUrl ?? null);
      setSelectedFile(null);
      setConfirmPassword("");
      setErrors({});
    } else {
      const initial = {
        id: "",
        name: "",
        email: "",
        profilePicUrl: "",
        role: "dealer",
        isAdmin: false,
        adminId: "",
        createdAt: new Date(),
      } as FormUserType;
      // For bulk mode we want an "empty" baseline so only changed fields are sent
      setForm(isBulk ? {} : initial);
      initialRef.current = isBulk ? {} : initial;
      setPreviewUrl(null);
      setSelectedFile(null);
      setConfirmPassword("");
      setErrors({});
    }
  }, [user, open, isBulk]);

  useEffect(() => {
    if (isDriverRoleSelected && form.isAdmin) {
      setForm((prev) => ({ ...prev, isAdmin: false }));
    }
  }, [isDriverRoleSelected, form.isAdmin]);

  useEffect(() => {
    if (!isBulk && (!form.role || form.role === "") && availableRoles.length > 0) {
      setForm((prev) => ({ ...prev, role: availableRoles[0] }));
    }
  }, [isBulk, form.role, availableRoles]);

  useEffect(() => {
    // cleanup object URL when preview changes/unmount
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleChange = (key: keyof FormUserType, value: any) => {
    setForm((s) => ({ ...s, [key]: value }));
    setErrors((e) => {
      const copy = { ...e };
      delete copy[String(key)];
      return copy;
    });
  };

  const handleFile = (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    // revoke previous blob url if we created one
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(url);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (f) handleFile(f);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] ?? null;
    if (f) handleFile(f);
  };

  const validate = (): boolean => {
    // Bulk: no required validation
    if (isBulk) {
      const next: Record<string, string> = {};
      if (form.password && String(form.password) !== "") {
        if (!confirmPassword || confirmPassword === "") next.confirmPassword = "Confirm password is required";
      }
      if (
        form.password !== undefined &&
        form.password !== "" &&
        confirmPassword !== undefined &&
        confirmPassword !== "" &&
        String(form.password) !== String(confirmPassword)
      ) {
        next.confirmPassword = "Passwords do not match";
      }
      setErrors(next);
      return Object.keys(next).length === 0;
    }

    const next: Record<string, string> = {};

    // name & email always required for create & single-edit
    if (!form.name || String(form.name).trim() === "") next.name = "Name is required";
    if (!form.email || String(form.email).trim() === "") next.email = "Email is required";
    else if (!emailRegex.test(String(form.email).trim())) next.email = "Invalid email";
    if ((isCreate || isEditSingle) && (!form.role || String(form.role).trim() === "")) {
      next.role = "Role is required";
    }

    // password fields:
    // - required for create
    // - optional for edit (user requested update password NOT required when editing)
    if (isCreate) {
      if (!form.password || String(form.password) === "") next.password = "Password is required";
      if (!confirmPassword || confirmPassword === "") next.confirmPassword = "Confirm password is required";
    } else {
      // editing single user: if password is provided then confirm is required and must match.
      if (form.password && String(form.password) !== "") {
        if (!confirmPassword || confirmPassword === "") next.confirmPassword = "Confirm password is required";
      }
    }

    // matching (only check when both present)
    if (
      form.password !== undefined &&
      form.password !== "" &&
      confirmPassword !== undefined &&
      confirmPassword !== "" &&
      String(form.password) !== String(confirmPassword)
    ) {
      next.confirmPassword = "Passwords do not match";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildChangedFieldsForEdit = (): Record<string, any> => {
    const allowedFields: Array<keyof FormUserType> = [
      "name",
      "role",
      "isAdmin",
      "profilePicUrl",
      "adminId",
      "email",
      "password"
    ];
    const fields: Record<string, any> = {};
    for (const k of allowedFields) {
      const newVal = (form as any)[k];
      const oldVal = (initialRef.current as any)[k];
      // include only when explicitly changed (treat undefined as untouched)
      if (newVal !== undefined && newVal !== null) {
        // ignore empty-string as "no value set" (prune)
        if (newVal === "") continue;
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) fields[String(k)] = newVal;
      }
    }
    return fields;
  };

  const handleSubmit = async () => {
    // validate according to mode
    if (!validate()) return;

    // Build output object (prune empty/undefined)
    if (isBulk) {
      // Only include fields that user actually changed compared to initialRef
      const allowedFields: Array<keyof FormUserType> = [
        "name",
        "role",
        "isAdmin",
        "profilePicUrl",
        "adminId",
        "email",
        "password"
      ];
      const fields: Record<string, any> = {};
      allowedFields.forEach((k) => {
        const newVal = (form as any)[k];
        const oldVal = (initialRef.current as any)[k];
        // include field only if newVal is defined and different from initial
        if (newVal !== undefined && newVal !== null) {
          // treat empty string as "not set" and don't include (consistent with pruning)
          if (newVal === "") return;
          const changed = JSON.stringify(newVal) !== JSON.stringify(oldVal);
          if (changed) fields[String(k)] = newVal;
        }
      });

      // If nothing changed, call onSave with empty object so caller can decide (or no-op)
      await onSave(fields);
      onClose();
      return;
    }

    // single-edit/create:
    if (isEditSingle) {
      // Build only changed fields (exclude password by default; password changes should be handled via update-password flow,
      // but allow including password here only if the user explicitly filled it — however AdminDashboard currently doesn't process password on edit)
      const changed = buildChangedFieldsForEdit();

      // If a password was provided while editing, we will include it in the output as 'password' (caller may handle or ignore)
      if (form.password && String(form.password) !== "") {
        // include password only if provided (optional for edits)
        changed.password = form.password;
      }

      if (Object.keys(changed).length === 0) {
        setErrors({ general: "No changes detected" });
        return;
      }

      await onSave(changed);
      onClose();
      return;
    }

    // create new user: send filled fields (pruned)
    const out: FormUserType = { ...form };
    Object.keys(out).forEach((k) => {
      const v = (out as any)[k];
      if (v === "" || v === undefined || v === null) delete (out as any)[k];
    });

    // include password (already in form) but don't include confirmPassword
    await onSave(out);
    onClose();
  };

  const passwordLabel = isEditSingle || isBulk ? "Update Password" : "Password";
  const confirmPasswordLabel = isEditSingle || isBulk ? "Confirm Update Password" : "Confirm Password";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isBulk ? "Edit Selected Users" : user ? "Edit User" : "Add User"}
      </DialogTitle>
      <DialogContent>
        {canEditAdminFlag && (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.isAdmin}
                onChange={(e) => handleChange("isAdmin", e.target.checked)}
                disabled={isDriverRoleSelected}
                title={
                  isDriverRoleSelected
                    ? "Drivers cannot be admins. Please assign a different role to grant admin access."
                    : undefined
                }
              />
            }
            label="Admin"
          />
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl
            fullWidth
            size="small"
            required={isCreate || isEditSingle}
            error={!!errors.role}
          >
            <InputLabel id="user-role-select-label">Role</InputLabel>
            <Select
              labelId="user-role-select-label"
              label="Role"
              value={form.role ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                handleChange("role", value === "" ? undefined : value);
              }}
              displayEmpty={isBulk}
            >
              {isBulk && (
                <MenuItem value="">
                  <em>Keep existing roles</em>
                </MenuItem>
              )}
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {formatRoleLabel(role)}
                </MenuItem>
              ))}
            </Select>
            {!!errors.role && <FormHelperText>{errors.role}</FormHelperText>}
          </FormControl>
          <TextField
            label="Name"
            value={form.name ?? ""}
            onChange={(e) => handleChange("name", e.target.value)}
            fullWidth
            required={isCreate || isEditSingle}
            error={!!errors.name}
            helperText={errors.name}
          />
          {!isBulk && (
            <TextField
              label="Email"
              value={form.email ?? ""}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
              // if bulk editing, don't allow editing the email field (it's per-user)
              required={isCreate || isEditSingle}
              error={!!errors.email}
              helperText={errors.email}
            />
          )}

          {/* Password fields: only relevant for single-create/edit */}
          <>
            <TextField
              label={passwordLabel}
              value={form.password ?? ""}
              type="password"
              onChange={(e) => handleChange("password", e.target.value)}
              fullWidth
              // required only when creating; optional when editing
              required={isCreate}
              error={!!errors.password}
              helperText={errors.password}
            />
            <TextField
              label={confirmPasswordLabel}
              value={confirmPassword}
              type="password"
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors((e) => {
                  const copy = { ...e };
                  delete copy.confirmPassword;
                  return copy;
                });
              }}
              fullWidth
              required={
                isCreate || (!!form.password && String(form.password) !== "")
              }
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
            />
          </>

          {/* Drag & drop / click-to-select image area */}
          <Typography component="label" sx={{ fontWeight: 600, mt: 2 }}>
            Profile Picture
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onInputChange}
          />
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: "pointer",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="preview"
                sx={{
                  maxHeight: 120,
                  maxWidth: 160,
                  objectFit: "cover",
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">
                  Drag & drop an image here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or click to select
                </Typography>
                <Typography variant="body2">
                  {selectedFile
                    ? selectedFile.name
                    : previewUrl
                      ? "Using existing image"
                      : "No image selected"}
                </Typography>
              </Box>
            )}
            <Box sx={{ flex: 1 }}></Box>
          </Box>
          {errors.general && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {errors.general}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {user ? "Save" : isBulk ? "Apply to Selected" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
